import type { CommunitiesBlockProps, Community } from '../types';
import { theme, sectionColors, cardGridClass, cssColor, cardTextColor } from '../theme';
import { richTextPlain } from '../richText';

/**
 * Pure communities index. Renders the tenant's SELECTED/featured communities
 * (host-provided from SiteConfig content.community_profiles — deterministic
 * discovery + listing association happen in the Communities workspace, never
 * here). Each card links to the community detail page (/communities/:slug),
 * which already exists on the tenant site. Framework-agnostic; graceful empty
 * state; never fetches.
 */
export function CommunitiesBlock({ props, communities }: { props: CommunitiesBlockProps; communities: Community[] }) {
  const { heading, limit, layout = 'grid', showImage = true } = props;
  const items = typeof limit === 'number' ? communities.slice(0, limit) : communities;
  const colors = sectionColors(props, { bg: 'transparent', text: theme.text });
  // The card's own colour (Card colour, else the site's Surface) and words readable on it.
  const cardBg = cssColor(props.cardBgColor);
  const cardLook = { backgroundColor: cardBg ?? theme.surface, color: cardTextColor(props, { cardBg, customCard: Boolean(cardBg) }) };

  if (items.length === 0) {
    return (
      <section className="px-6 py-12 text-center" style={{ ...colors, fontFamily: theme.bodyFont }}>
        <p className="opacity-60">No communities to show yet.</p>
      </section>
    );
  }

  const grid = cardGridClass({ columns: props.columns, layout });

  return (
    <section className="px-6 py-12" style={{ ...colors, fontFamily: theme.bodyFont }}>
      {heading ? (
        <h2 className="mx-auto mb-8 max-w-6xl text-2xl font-semibold" style={{ fontFamily: theme.headingFont }}>
          {heading}
        </h2>
      ) : null}
      <div className={`mx-auto grid max-w-6xl gap-6 ${grid}`}>
        {items.map((c) => {
          const place = [c.city, c.state].filter(Boolean).join(', ');
          const blurb = richTextPlain(c.description) || richTextPlain(c.local_intro);
          return (
            <a
              key={c.slug || c.name}
              href={`/communities/${encodeURIComponent((c.slug || '').replace(/^\//, ''))}`}
              className="blk-card block overflow-hidden rounded-lg border border-black/10 transition hover:shadow-md"
              style={cardLook}
            >
              {showImage && c.hero_image_url ? (
                <img src={c.hero_image_url} alt={c.name} referrerPolicy="no-referrer" loading="lazy" decoding="async" className="h-44 w-full object-cover" />
              ) : null}
              <div className="p-4">
                <div className="text-lg font-semibold" style={{ fontFamily: theme.headingFont }}>
                  {c.name}
                </div>
                {place ? <div className="text-sm opacity-60">{place}</div> : null}
                {blurb ? <p className="mt-2 line-clamp-3 text-sm opacity-75">{blurb}</p> : null}
                <span className="blk-link mt-3 inline-block text-sm font-medium" style={{ color: theme.primary }}>
                  Explore {c.name} →
                </span>
              </div>
            </a>
          );
        })}
      </div>
    </section>
  );
}
